import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle, User, Mail, Phone, ArrowLeft } from 'lucide-react';
import AuthModal from '../../components/AuthModal';
import { auth } from '../../lib/firebase';

const RegisterInfo = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: ''
  });

  // Get plan info from URL params and localStorage
  const planType = searchParams.get('plan') || 'free';
  const paymentId = searchParams.get('payment');
  const source = searchParams.get('source') || 'edumi';
  const amount = searchParams.get('amount') || '0';

  // Coursedee ไม่ใช้หน้านี้แล้ว (เปลี่ยนเป็น login-before-buy) — เด้งออกทันที ไม่ให้หน้านี้โผล่
  // dashboard จะจัดการเรื่อง login เอง (ยังไม่ล็อกอิน → เด้งไป /login)
  useEffect(() => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isCoursedee = source === 'coursedee' || source === 'coursedee_cart'
      || hostname.includes('aiforyou');
    if (isCoursedee) {
      navigate('/coursedee/dashboard', { replace: true });
    }
  }, [source, navigate]);

  console.log('[RegisterInfo v3.1] URL params - plan:', searchParams.get('plan'), 'planType:', planType, 'source:', source);
  
  // Get checkout data from localStorage - check all possible keys
  const subscriptionData = localStorage.getItem('subscriptionCheckout');
  const salePageData = localStorage.getItem('salePageCheckout');
  const coursedeeData = localStorage.getItem('coursedeeCartCheckout');
  
  let savedCheckoutData: any = null;
  
  // Try to parse from different sources
  if (coursedeeData) {
    try {
      savedCheckoutData = JSON.parse(coursedeeData);
      console.log('[RegisterInfo] Using coursedeeCartCheckout:', savedCheckoutData);
    } catch (err) {
      console.error('Error parsing coursedee checkout data:', err);
    }
  } else if (salePageData) {
    try {
      savedCheckoutData = JSON.parse(salePageData);
      console.log('[RegisterInfo] Using salePageCheckout:', savedCheckoutData);
    } catch (err) {
      console.error('Error parsing salepage checkout data:', err);
    }
  } else if (subscriptionData) {
    try {
      savedCheckoutData = JSON.parse(subscriptionData);
      console.log('[RegisterInfo] Using subscriptionCheckout:', savedCheckoutData);
    } catch (err) {
      console.error('Error parsing subscription checkout data:', err);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate inputs
      if (!formData.fullName.trim()) {
        throw new Error('กรุณากรอกชื่อ-นามสกุล');
      }
      if (!formData.email.trim() || !formData.email.includes('@')) {
        throw new Error('กรุณากรอกอีเมลให้ถูกต้อง');
      }
      if (!formData.phone.trim() || formData.phone.length < 10) {
        throw new Error('กรุณากรอกเบอร์โทรศัพท์ให้ถูกต้อง (10 หลัก)');
      }

      // Get tier from saved checkout data (needed early for package creation)
      const tier = savedCheckoutData?.tier || planType;

      // Extract username from email
      let username = formData.email.split('@')[0];
      
      // Get last 6 digits of phone as password
      const password = formData.phone.slice(-6);

      // Create Firebase Auth account
      const { auth } = await import('../../lib/firebase');
      const { createUserWithEmailAndPassword, updateProfile } = await import('firebase/auth');
      
      // Check if email already exists in Firestore
      const { db } = await import('../../lib/firebase');
      const { collection, query, where, getDocs, doc, setDoc, getDoc, serverTimestamp, Timestamp } = await import('firebase/firestore');
      
      const usersCollection = collection(db, 'users');
      const emailQuery = query(usersCollection, where('email', '==', formData.email));
      const emailSnapshot = await getDocs(emailQuery);
      
      let user;
      let isExistingUser = false;
      
      // Check if user already exists in Firestore
      if (!emailSnapshot.empty) {
        console.log('[RegisterInfo] User already exists in Firestore, will update with new purchase');
        isExistingUser = true;
        
        // Try to sign in instead of creating new account
        const { signInWithEmailAndPassword } = await import('firebase/auth');
        try {
          const userCredential = await signInWithEmailAndPassword(auth, formData.email, password);
          user = userCredential.user;
          console.log('[RegisterInfo] Signed in existing user:', user.uid);
        } catch (signInError: any) {
          if (signInError.code === 'auth/wrong-password') {
            setError('❌ รหัสผ่านไม่ถูกต้อง\n\nกรุณาใช้รหัสผ่านเดิมของคุณ หรือคลิก "เข้าสู่ระบบ" เพื่อรีเซ็ตรหัสผ่าน');
          } else {
            setError('❌ ไม่สามารถเข้าสู่ระบบได้\n\nกรุณาคลิก "เข้าสู่ระบบ" ด้านบนเพื่อเข้าสู่ระบบ');
          }
          setLoading(false);
          return;
        }
      } else {
        // Create new user
        let userCredential;
        try {
          userCredential = await createUserWithEmailAndPassword(auth, formData.email, password);
        } catch (authError: any) {
          if (authError.code === 'auth/email-already-in-use') {
            // Email exists in Auth but not in Firestore - try to sign in
            const { signInWithEmailAndPassword } = await import('firebase/auth');
            try {
              const signInCredential = await signInWithEmailAndPassword(auth, formData.email, password);
              user = signInCredential.user;
              isExistingUser = true;
              console.log('[RegisterInfo] Signed in existing Auth user:', user.uid);
            } catch (signInError: any) {
              setError('❌ พบอีเมลนี้ในระบบแล้ว แต่รหัสผ่านไม่ถูกต้อง\n\nกรุณาคลิก "เข้าสู่ระบบ" ด้านบน');
              setLoading(false);
              return;
            }
          } else {
            throw authError;
          }
        }
        
        if (!user) {
          user = userCredential.user;
          
          // Update display name for new user
          await updateProfile(user, {
            displayName: formData.fullName
          });
        }
      }

      // Check if username already exists, if so add number
      const usernameRef = doc(db, 'usernames', username);
      const usernameDoc = await getDoc(usernameRef);
      
      if (usernameDoc.exists()) {
        // Username taken, find available one
        let counter = 2;
        let newUsername = `${username}${counter}`;
        let newUsernameRef = doc(db, 'usernames', newUsername);
        let newUsernameDoc = await getDoc(newUsernameRef);
        
        while (newUsernameDoc.exists()) {
          counter++;
          newUsername = `${username}${counter}`;
          newUsernameRef = doc(db, 'usernames', newUsername);
          newUsernameDoc = await getDoc(newUsernameRef);
        }
        
        username = newUsername;
      }
      
      // Reserve username (only for new users)
      if (!isExistingUser) {
        await setDoc(doc(db, 'usernames', username), {
          userId: user.uid,
          createdAt: serverTimestamp()
        });
      }

      // Determine subscription based on current hostname
      const hostname = window.location.hostname;
      let subscriptionKey = 'edumi';
      
      // Check for AI product domain (aiforyou)
      if (hostname.includes('aiforyou')) {
        subscriptionKey = 'coursedee';
      } else if (hostname.includes('salepage')) {
        subscriptionKey = 'salepage';
      }
      
      console.log('[RegisterInfo] Subscription key:', subscriptionKey, 'hostname:', hostname);
      console.log('[RegisterInfo] Is existing user:', isExistingUser);
      
      // Calculate end date for free trial (14 days)
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 14);

      // For existing users, get current data and update
      let userData: any;
      
      if (isExistingUser) {
        // Get existing user data
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        if (userDocSnap.exists()) {
          userData = userDocSnap.data();
          userData.updatedAt = serverTimestamp();
          
          // For Coursedee: Add new packages to existing coursedeePackages
          if (subscriptionKey === 'coursedee') {
            let newPackages: any[] = [];
            
            // Check if this is from Coursedee Cart with items
            if (savedCheckoutData?.isCoursedeeCart && savedCheckoutData?.items) {
              newPackages = savedCheckoutData.items.map((item: any) => ({
                id: item.id,
                name: item.name,
                version: item.version,
                price: item.price,
                finalPrice: item.finalPrice !== null && item.finalPrice !== undefined ? item.finalPrice : item.price,
                purchaseDate: Timestamp.now(),
                paymentId: paymentId
              }));
            } else if (tier && tier !== 'free') {
              // Regular Coursedee subscription purchase
              const version = tier.toLowerCase().includes('v2') ? 'v2' : 'v1';
              
              // Get package name (same logic as new user)
              let packageName = `AIForYou ${version.toUpperCase()}`;
              if (savedCheckoutData?.items && savedCheckoutData.items.length > 0) {
                packageName = savedCheckoutData.items[0].name;
              } else if (savedCheckoutData?.planName) {
                packageName = savedCheckoutData.planName;
              } else if (packageName === 'AIForYou V1') {
                packageName = 'AIForYou Content Builder (V1)';
              } else if (packageName === 'AIForYou V2') {
                packageName = 'AIForYou Brand Builder (V2)';
              }
              
              newPackages = [{
                id: paymentId || `coursedee_${Date.now()}`,
                name: packageName,
                version: version,
                price: parseInt(amount) || 0,
                finalPrice: savedCheckoutData?.finalAmount !== null && savedCheckoutData?.finalAmount !== undefined 
                  ? savedCheckoutData.finalAmount 
                  : parseInt(amount) || 0,
                purchaseDate: Timestamp.now(),
                paymentId: paymentId
              }];
            }
            
            // Merge with existing packages
            if (newPackages.length > 0) {
              if (userData.coursedeePackages && Array.isArray(userData.coursedeePackages)) {
                userData.coursedeePackages = [...userData.coursedeePackages, ...newPackages];
              } else {
                userData.coursedeePackages = newPackages;
              }
              console.log('[RegisterInfo] Added coursedeePackages:', newPackages);
            }
          }
        } else {
          // User exists in Auth but not in Firestore - create new document
          userData = {
            email: formData.email,
            fullName: formData.fullName,
            displayName: formData.fullName,
            phone: formData.phone,
            username: username,
            emailVerified: false,
            signupSource: subscriptionKey,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            subscriptions: {}
          };
          
          // For Coursedee: Add coursedeePackages
          if (subscriptionKey === 'coursedee') {
            let newPackages: any[] = [];
            
            // Check if this is from Coursedee Cart with items
            if (savedCheckoutData?.isCoursedeeCart && savedCheckoutData?.items) {
              newPackages = savedCheckoutData.items.map((item: any) => ({
                id: item.id,
                name: item.name,
                version: item.version,
                price: item.price,
                finalPrice: item.finalPrice !== null && item.finalPrice !== undefined ? item.finalPrice : item.price,
                purchaseDate: Timestamp.now(),
                paymentId: paymentId
              }));
            } else if (tier && tier !== 'free') {
              // Regular Coursedee subscription purchase
              const version = tier.toLowerCase().includes('v2') ? 'v2' : 'v1';
              
              // Get package name (same logic as new user)
              let packageName = `AIForYou ${version.toUpperCase()}`;
              if (savedCheckoutData?.items && savedCheckoutData.items.length > 0) {
                packageName = savedCheckoutData.items[0].name;
              } else if (savedCheckoutData?.planName) {
                packageName = savedCheckoutData.planName;
              } else if (packageName === 'AIForYou V1') {
                packageName = 'AIForYou Content Builder (V1)';
              } else if (packageName === 'AIForYou V2') {
                packageName = 'AIForYou Brand Builder (V2)';
              }
              
              newPackages = [{
                id: paymentId || `coursedee_${Date.now()}`,
                name: packageName,
                version: version,
                price: parseInt(amount) || 0,
                finalPrice: savedCheckoutData?.finalAmount !== null && savedCheckoutData?.finalAmount !== undefined 
                  ? savedCheckoutData.finalAmount 
                  : parseInt(amount) || 0,
                purchaseDate: Timestamp.now(),
                paymentId: paymentId
              }];
            }
            
            if (newPackages.length > 0) {
              userData.coursedeePackages = newPackages;
              console.log('[RegisterInfo] Created coursedeePackages:', newPackages);
            }
          }
        }
      } else {
        // Create new user document
        userData = {
          email: formData.email,
          fullName: formData.fullName,
          displayName: formData.fullName,
          phone: formData.phone,
          username: username,
          emailVerified: false,
          signupSource: subscriptionKey, // 'coursedee', 'salepage', or 'edumi'
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          subscriptions: {}
        };
        
        // For Coursedee: Add coursedeePackages
        if (subscriptionKey === 'coursedee') {
          let newPackages: any[] = [];
          
          // Check if this is from Coursedee Cart with items
          if (savedCheckoutData?.isCoursedeeCart && savedCheckoutData?.items) {
            newPackages = savedCheckoutData.items.map((item: any) => ({
              id: item.id,
              name: item.name,
              version: item.version,
              price: item.price,
              finalPrice: item.finalPrice !== null && item.finalPrice !== undefined ? item.finalPrice : item.price,
              purchaseDate: Timestamp.now(),
              paymentId: paymentId
            }));
          } else if (tier && tier !== 'free') {
            // Regular Coursedee subscription purchase
            const version = tier.toLowerCase().includes('v2') ? 'v2' : 'v1';
            
            // Get package name from savedCheckoutData if available (PRIORITY ORDER)
            let packageName = `AIForYou ${version.toUpperCase()}`; // Default fallback
            
            // Priority 1: Use items array (has full name)
            if (savedCheckoutData?.items && savedCheckoutData.items.length > 0) {
              packageName = savedCheckoutData.items[0].name;
            } 
            // Priority 2: Use planName
            else if (savedCheckoutData?.planName) {
              packageName = savedCheckoutData.planName;
            }
            // Priority 3: Expand short name to full name
            else if (packageName === 'AIForYou V1') {
              packageName = 'AIForYou Content Builder (V1)';
            } else if (packageName === 'AIForYou V2') {
              packageName = 'AIForYou Brand Builder (V2)';
            }
            
            newPackages = [{
              id: paymentId || `coursedee_${Date.now()}`,
              name: packageName,
              version: version,
              price: parseInt(amount) || 0,
              finalPrice: savedCheckoutData?.finalAmount !== null && savedCheckoutData?.finalAmount !== undefined 
                ? savedCheckoutData.finalAmount 
                : parseInt(amount) || 0,
              purchaseDate: Timestamp.now(),
              paymentId: paymentId
            }];
          }
          
          if (newPackages.length > 0) {
            userData.coursedeePackages = newPackages;
            console.log('[RegisterInfo] Created coursedeePackages for new user:', newPackages);
          }
        }
      }

      // Add subscription for the current domain
      // Use saved checkout data if available (for free activation with discount)
      const isFreeActivation = savedCheckoutData?.isFreeActivation === true;
      
      // Ensure subscriptions object exists
      if (!userData.subscriptions) {
        userData.subscriptions = {};
      }
      
      if (planType === 'free' && !isFreeActivation) {
        // Regular free trial
        userData.subscriptions[subscriptionKey] = {
          tier: 'free',
          status: 'trial',
          startDate: Timestamp.now(),
          endDate: Timestamp.fromDate(endDate),
          createdAt: serverTimestamp()
        };
      } else if (isFreeActivation) {
        // Free activation with 100% discount code
        const activationEndDate = new Date();
        if (tier.includes('yearly')) {
          activationEndDate.setFullYear(activationEndDate.getFullYear() + 1);
        } else if (tier.includes('semi_annual')) {
          activationEndDate.setMonth(activationEndDate.getMonth() + 6);
        } else {
          activationEndDate.setMonth(activationEndDate.getMonth() + 1);
        }
        
        userData.subscriptions[subscriptionKey] = {
          tier: tier,
          status: 'active',
          isFreeActivation: true,
          startDate: Timestamp.now(),
          endDate: Timestamp.fromDate(activationEndDate),
          discountCode: savedCheckoutData?.discountCode || null,
          discountPercent: savedCheckoutData?.discountPercent || 100,
          originalAmount: savedCheckoutData?.originalAmount || 0,
          finalAmount: 0,
          createdAt: serverTimestamp()
        };
      } else {
        // Paid plan (pending payment)
        userData.subscriptions[subscriptionKey] = {
          tier: tier,
          status: 'pending',
          paymentId: paymentId || null,
          amount: parseInt(amount),
          originalAmount: savedCheckoutData?.originalAmount || parseInt(amount),
          finalAmount: savedCheckoutData?.finalAmount !== null && savedCheckoutData?.finalAmount !== undefined 
            ? savedCheckoutData.finalAmount 
            : parseInt(amount),
          discountCode: savedCheckoutData?.discountCode || null,
          discountPercent: savedCheckoutData?.discountPercent || 0,
          createdAt: serverTimestamp()
        };
      }

      await setDoc(doc(db, 'users', user.uid), userData);

      // If this is a paid plan and we have payment ID, update payment log
      if (paymentId && planType !== 'free') {
        try {
          const { updateDoc } = await import('firebase/firestore');
          const paymentRef = doc(db, 'paymentLogs', paymentId);
          await updateDoc(paymentRef, {
            userId: user.uid,
            userEmail: formData.email,
            userName: formData.fullName,
            userPhone: formData.phone,
            username: username,
            updatedAt: serverTimestamp()
          });
        } catch (err) {
          console.error('Error updating payment log:', err);
          // Don't fail the registration if payment update fails
        }
      }

      // For Coursedee Cart: Create payment record in payments collection
      if (subscriptionKey === 'coursedee' && paymentId && savedCheckoutData?.isCoursedeeCart) {
        try {
          const { setDoc, doc } = await import('firebase/firestore');
          
          // Create payment record in payments collection
          const paymentRef = doc(db, 'payments', paymentId);
          await setDoc(paymentRef, {
            userId: user.uid,
            email: formData.email,
            fullName: formData.fullName,
            phone: formData.phone,
            username: username,
            items: savedCheckoutData.items || [],
            totalAmount: parseInt(amount),
            status: 'verified',
            paymentMethod: 'promptpay',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          console.log('[RegisterInfo] ✅ Created payment record in payments collection');
        } catch (err) {
          console.error('[RegisterInfo] Error creating payment record:', err);
          // Don't fail the registration if payment creation fails
        }
      }
      
      // For Coursedee: Update subscription_payments with email and userId
      if (subscriptionKey === 'coursedee' && paymentId && !savedCheckoutData?.isCoursedeeCart) {
        try {
          const { updateDoc, collection, query, where, getDocs } = await import('firebase/firestore');
          
          // Find the payment record by paymentId
          const paymentsRef = collection(db, 'subscription_payments');
          const paymentsQuery = query(paymentsRef, where('__name__', '==', paymentId));
          const paymentsSnapshot = await getDocs(paymentsQuery);
          
          if (!paymentsSnapshot.empty) {
            const paymentDocRef = paymentsSnapshot.docs[0].ref;
            await updateDoc(paymentDocRef, {
              email: formData.email,
              userId: user.uid,
              userName: formData.fullName,
              userPhone: formData.phone,
              username: username,
              updatedAt: serverTimestamp()
            });
            console.log('[RegisterInfo] ✅ Updated subscription_payments with email and userId');
          } else {
            console.log('[RegisterInfo] ⚠️ Payment record not found in subscription_payments');
          }
        } catch (err) {
          console.error('[RegisterInfo] Error updating subscription_payments:', err);
          // Don't fail the registration if payment update fails
        }
      }

      // Send verification email via Cloudflare Worker (direct call)
      try {
        // Generate custom verification link
        const verifyToken = btoa(JSON.stringify({
          email: formData.email,
          uid: user.uid,
          timestamp: Date.now()
        }));
        
        let verifyLink = `https://aiforyou.web.app/verify-email?token=${verifyToken}`;
        if (subscriptionKey === 'coursedee') {
          verifyLink = `https://aiforyou.web.app/verify-email?token=${verifyToken}`;
        } else if (subscriptionKey === 'salepage') {
          verifyLink = `https://saledi.web.app/verify-email?token=${verifyToken}`;
        }
        
        console.log('[RegisterInfo] Calling Cloudflare Worker directly...');
        const response = await fetch('https://edumi-verification-email.edumi-subdomin.workers.dev', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formData.email,
            displayName: formData.fullName,
            verifyLink: verifyLink
          })
        });
        
        const result = await response.json();
        
        if (result.success) {
          console.log('[RegisterInfo] ✅ Verification email sent via Cloudflare Worker');
        } else {
          console.error('[RegisterInfo] Worker error:', result.error);
        }
      } catch (err) {
        console.error('[RegisterInfo] Error sending verification email:', err);
        // Don't fail registration if email fails
      }

      // Note: Welcome email will be sent automatically by Cloud Function
      // after user verifies their email (sendWelcomeEmailOnVerify)
      console.log('[RegisterInfo] ✅ Account created. User is logged in. Welcome email will be sent after email verification.');

      // User is already logged in (createUserWithEmailAndPassword logs them in automatically)
      // Redirect to success page
      // For Coursedee: Add package info to URL
      let redirectUrl = `/subscription/register-success?username=${formData.email}&password=${password}&email=${formData.email}&plan=${planType}&source=${subscriptionKey}`;
      
      if (subscriptionKey === 'coursedee' && userData.coursedeePackages && userData.coursedeePackages.length > 0) {
        const packageNames = userData.coursedeePackages.map((pkg: any) => pkg.name).join(', ');
        redirectUrl += `&packageName=${encodeURIComponent(packageNames)}`;
      }
      
      // Add discount info from savedCheckoutData if available
      if (savedCheckoutData) {
        if (savedCheckoutData.discountCode) {
          redirectUrl += `&discountCode=${encodeURIComponent(savedCheckoutData.discountCode)}`;
        }
        if (savedCheckoutData.discountPercent !== null && savedCheckoutData.discountPercent !== undefined) {
          redirectUrl += `&discountPercent=${savedCheckoutData.discountPercent}`;
        }
        if (savedCheckoutData.originalAmount !== null && savedCheckoutData.originalAmount !== undefined) {
          redirectUrl += `&originalAmount=${savedCheckoutData.originalAmount}`;
        }
        if (savedCheckoutData.finalAmount !== null && savedCheckoutData.finalAmount !== undefined) {
          redirectUrl += `&finalAmount=${savedCheckoutData.finalAmount}`;
        }
      }
      
      // Clear cart and localStorage for Coursedee
      if (subscriptionKey === 'coursedee') {
        localStorage.removeItem('coursedeeCartCheckout');
        localStorage.removeItem('coursedeeCart');
        localStorage.removeItem('edumi_cart'); // Clear CartContext storage
        
        // Dispatch event to reload cart in CartContext
        window.dispatchEvent(new Event('cart-reload'));
        
        console.log('[RegisterInfo] ✅ Cleared Coursedee cart and localStorage');
      }
      
      navigate(redirectUrl);
      
      // Note: User stays logged in, but emailVerified is false until they click the link
      
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการสมัครสมาชิก กรุณาลองใหม่อีกครั้ง');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(''); // Clear error when user types
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 py-12 px-4">
      <div className="max-w-md mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">ย้อนกลับ</span>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            ยืนยันข้อมูลการสมัครใช้งาน
          </h1>
          <p className="text-gray-600">
            กรุณากรอกข้อมูลเพื่อสร้างบัญชีของคุณ
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Login Link */}
          <div className="text-center mb-6 pb-6 border-b border-gray-200">
            <p className="text-gray-600 text-sm">
              💡 มีบัญชีอยู่แล้ว?{' '}
              <button
                type="button"
                onClick={() => setShowLoginModal(true)}
                className="text-blue-600 hover:text-blue-700 underline font-medium transition-colors"
              >
                เข้าสู่ระบบ
              </button>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                ชื่อ-นามสกุล <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  placeholder="กรอกชื่อ-นามสกุล"
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all outline-none"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                อีเมล <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="example@email.com"
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all outline-none"
                  disabled={loading}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                ใช้สำหรับยืนยันตัวตนและรับข้อมูลข่าวสาร
              </p>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                เบอร์โทรศัพท์ <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, ''))}
                  placeholder="0812345678"
                  maxLength={10}
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all outline-none"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-700">{error}</p>
                  {error.includes('ถูกใช้งานแล้ว') && (
                    <button
                      onClick={() => navigate('/login')}
                      className="text-sm text-red-600 underline mt-1 hover:text-red-700"
                    >
                      เข้าสู่ระบบ
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  กำลังสมัครสมาชิก...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  ยืนยันการสมัครใช้งาน
                </>
              )}
            </button>
          </form>

          {/* Info Box */}
          <div className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-800">
              <strong>หมายเหตุ:</strong> หลังจากสมัครสมาชิก คุณจะได้รับอีเมลยืนยันตัวตน 
              กรุณาตรวจสอบอีเมลและคลิกลิงก์เพื่อเริ่มใช้งาน
            </p>
          </div>
        </div>

        {/* Login Modal */}
        <AuthModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          mode="login"
          onLoginSuccess={async () => {
            try {
              setShowLoginModal(false);
              setLoading(true);

              // Link payment to user account
              if (paymentId && auth.currentUser) {
                const { db } = await import('../../lib/firebase');
                const { doc, updateDoc, getDoc } = await import('firebase/firestore');
                
                // Update subscription payment
                const paymentRef = doc(db, 'subscriptionPayments', paymentId);
                const paymentDoc = await getDoc(paymentRef);
                
                if (paymentDoc.exists()) {
                  await updateDoc(paymentRef, {
                    userId: auth.currentUser.uid,
                    email: auth.currentUser.email,
                    updatedAt: new Date()
                  });
                  console.log('[RegisterInfo] Payment linked to user:', auth.currentUser.uid);
                }
              }

              // Redirect to dashboard
              const hostname = window.location.hostname;
              if (hostname.includes('aiforyou')) {
                window.location.href = 'https://aiforyou.web.app/instructor/dashboard';
              } else if (hostname.includes('salepage')) {
                navigate('/salepage/dashboard');
              } else {
                navigate('/instructor/dashboard');
              }
            } catch (err) {
              console.error('[RegisterInfo] Error linking payment:', err);
              navigate('/instructor/dashboard');
            }
          }}
        />
      </div>
    </div>
  );
};

export default RegisterInfo;
